import { useCallback, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import Chip from 'components/Chip/Chip';
import Input from 'components/Input/Input';
import { cn } from 'components/lib/utils';

import { ProjectCategory } from '../../views/Projects/constants';

export const EditableTagsCell = ({
  tags,
  rowId,
  category,
  onTagsUpdate
}: {
  tags: string[];
  rowId: string;
  category: ProjectCategory;
  onTagsUpdate?: (
    itemId: string,
    category: ProjectCategory,
    tags: string[]
  ) => void;
}) => {
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const isEditing = editingRowId === rowId;
  const isHovered = hoveredRowId === rowId;

  const resetEditingState = useCallback(() => {
    setHoveredRowId(null);
    setEditingRowId(null);
    setEditValue('');
  }, []);

  const handleSaveTags = useCallback(() => {
    const newTags = editValue
      .split(',')
      .map((tag: string) => tag.trim())
      .filter((tag: string) => tag.length > 0);

    if (onTagsUpdate) {
      onTagsUpdate(rowId, category, newTags);
    }

    resetEditingState();
  }, [editValue, onTagsUpdate, rowId, category, resetEditingState]);

  if (isEditing) {
    return (
      <Input
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={resetEditingState}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSaveTags();
          } else if (e.key === 'Escape') {
            resetEditingState();
          }
        }}
        autoFocus
      />
    );
  }

  return (
    <div
      className="flex gap-1 text-sm text-gray-900 group"
      onClick={(e) => {
        e.stopPropagation();
        setEditingRowId(rowId);
        setEditValue(tags.join(', '));
      }}
      onMouseEnter={() => setHoveredRowId(rowId)}
      onMouseLeave={() => setHoveredRowId(null)}
    >
      <FontAwesomeIcon
        icon="fa-solid fa-pen"
        className="my-auto mr-1 opacity-50 text-[10px] group-hover:opacity-100 group-hover:text-indigo-700"
      />
      {tags.length > 0 ? (
        tags.map((tag: string) => (
          <Chip
            key={tag}
            variant="flat"
            status="secondary"
            size="sm"
            className={cn(
              '!max-w-[80px]',
              isHovered && 'bg-indigo-700 text-white'
            )}
          >
            <span className="truncate text-left overflow-hidden whitespace-nowrap block w-full">
              {tag}
            </span>
          </Chip>
        ))
      ) : (
        <span className="text-base-mutedForeground text-p-s hover:text-indigo-700">
          Add tags
        </span>
      )}
    </div>
  );
};
